import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { Button, Calendar, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DropdownMenu, Input, Label, } from '@wit-support/ui';
import { useAdminTimezone, wallClockToUtcIso, startOfTodayInZone, NonexistentWallClockError, } from '@wit-support/ui-domain';
import { CancelButton } from '@wit-support/ui-forms';
import { ChevronDown, Clock } from 'lucide-react';
// Wall-clock → UTC conversion (two-pass, DST-correct) + the admin-tz
// "start of today" guard live in ui-domain/lib/time.tsx — shared with
// FilterBar's date-range picker (see M-021). Both need the same Intl-offset
// technique; this file only supplies the y/m/d/hh/mm the admin picked.
// ---------------------------------------------------------------------------
// Preset label map
// ---------------------------------------------------------------------------
const PRESET_LABELS = {
    '+30m': '+30 мин',
    '+1d': '+1 день',
    '+2d': '+2 дня',
    '+3d': '+3 дня',
    '+4d': '+4 дня',
};
const PRESETS = ['+30m', '+1d', '+2d', '+3d', '+4d'];
function CustomDatetimeDialog({ open, onOpenChange, onConfirm }) {
    // The admin's configured timezone — the chosen wall-clock time is interpreted
    // in THIS zone, not the browser's, so an admin in a different tz than the
    // browser gets the wall-clock they actually picked.
    const zone = useAdminTimezone();
    // Re-evaluate when the dialog opens so the min-date is never stale if the
    // dialog was left open past local midnight (the server+client guards already
    // block past submission; this only affects the visual grey-out).
    const minDay = useMemo(() => startOfTodayInZone(zone), [zone, open]);
    const [selectedDate, setSelectedDate] = useState(undefined);
    const [time, setTime] = useState('');
    const [error, setError] = useState(null);
    function handleConfirm() {
        setError(null);
        if (!selectedDate) {
            setError('Выберите дату');
            return;
        }
        if (!time.match(/^\d{2}:\d{2}$/)) {
            // The input is native `type="time"`, so this only fires on an empty value.
            setError('Укажите время');
            return;
        }
        const [hh, mm] = time.split(':').map(Number);
        // The Calendar yields a browser-local Date; read its wall-clock y/m/d and
        // resolve (y,m,d,hh,mm) to a UTC instant IN THE ADMIN TIMEZONE.
        let isoAt;
        try {
            isoAt = wallClockToUtcIso(selectedDate.getFullYear(), selectedDate.getMonth() + 1, selectedDate.getDate(), hh, mm, zone);
        }
        catch (err) {
            if (err instanceof NonexistentWallClockError) {
                setError('Указанное время не существует (переход на летнее время)');
                return;
            }
            throw err;
        }
        const minTime = Date.now() + 60_000;
        // `<=` mirrors the server guard (it rejects `<= now+60s`), so the
        // exactly-now+60s boundary is rejected client-side too (no drift).
        if (Date.parse(isoAt) <= minTime) {
            setError('Время должно быть не менее чем через 1 минуту');
            return;
        }
        onConfirm(isoAt);
        // Reset state after confirmed
        setSelectedDate(undefined);
        setTime('');
    }
    function handleOpenChange(o) {
        if (!o) {
            // Reset all inputs on close so reopening starts fresh (no stale date/time).
            setError(null);
            setSelectedDate(undefined);
            setTime('');
        }
        onOpenChange(o);
    }
    return (_jsx(Dialog, { open: open, onOpenChange: handleOpenChange, children: _jsxs(DialogContent, { size: "sm", children: [_jsxs(DialogHeader, { className: "gap-4", children: [_jsx(DialogTitle, { children: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0432\u0440\u0435\u043C\u044F \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u0438" }), _jsx(DialogDescription, { children: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0434\u0430\u0442\u0443 \u0438 \u0432\u0440\u0435\u043C\u044F \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u0438 \u043F\u0438\u0441\u044C\u043C\u0430. \u0414\u043E\u043B\u0436\u043D\u043E \u0431\u044B\u0442\u044C \u043D\u0435 \u043C\u0435\u043D\u0435\u0435 1 \u043C\u0438\u043D\u0443\u0442\u044B \u043E\u0442 \u0442\u0435\u043A\u0443\u0449\u0435\u0433\u043E \u043C\u043E\u043C\u0435\u043D\u0442\u0430." })] }), _jsxs(DialogBody, { children: [_jsx(Calendar, { mode: "single", selected: selectedDate, onSelect: (d) => setSelectedDate(d), disabled: (d) => d.getTime() < minDay }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(Label, { htmlFor: "schedule-time", children: "\u0412\u0440\u0435\u043C\u044F" }), _jsx(Input, { id: "schedule-time", type: "time", value: time, onChange: (e) => {
                                        setTime(e.target.value);
                                        setError(null);
                                    }, "aria-label": "\u0412\u0440\u0435\u043C\u044F \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u0438", "data-testid": "schedule-time-input" })] }), error ? (_jsx("p", { className: "text-xs text-[var(--color-state-danger-fg)]", role: "alert", "data-testid": "schedule-error", children: error })) : null] }), _jsxs(DialogFooter, { children: [_jsx(CancelButton, { onClick: () => handleOpenChange(false) }), _jsx(Button, { type: "button", onClick: handleConfirm, "data-testid": "schedule-confirm", children: "\u0417\u0430\u043F\u043B\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u0442\u044C" })] })] }) }));
}
export function ScheduleControl({ onSchedule, disabled = false }) {
    const [customOpen, setCustomOpen] = useState(false);
    function handlePreset(preset) {
        onSchedule({ preset });
    }
    function handleCustomConfirm(isoAt) {
        setCustomOpen(false);
        onSchedule({ at: isoAt });
    }
    return (_jsxs(_Fragment, { children: [_jsxs(DropdownMenu.Root, { children: [_jsx(DropdownMenu.Trigger, { asChild: true, children: _jsxs(Button, { type: "button", variant: "secondary", disabled: disabled, "aria-label": "\u0417\u0430\u043F\u043B\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u0443", "data-testid": "schedule-dropdown-trigger", children: [_jsx(Clock, { className: "h-4 w-4", "aria-hidden": "true" }), "\u0417\u0430\u043F\u043B\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u0442\u044C", _jsx(ChevronDown, { className: "ml-1 h-3.5 w-3.5 opacity-70", "aria-hidden": "true" })] }) }), _jsxs(DropdownMenu.Content, { align: "end", children: [PRESETS.map((p) => (_jsx(DropdownMenu.Item, { onSelect: () => handlePreset(p), "data-testid": `preset-${p}`, children: PRESET_LABELS[p] }, p))), _jsx(DropdownMenu.Separator, {}), _jsx(DropdownMenu.Item, { onSelect: () => setCustomOpen(true), "data-testid": "preset-custom", children: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0432\u0440\u0435\u043C\u044F\u2026" })] })] }), _jsx(CustomDatetimeDialog, { open: customOpen, onOpenChange: setCustomOpen, onConfirm: handleCustomConfirm })] }));
}
//# sourceMappingURL=schedule-control.js.map